import { useMemo } from 'react';
import omit from 'lodash/omit';

import { useFilters } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { OMITTED_FIELDS } from 'Stores/SavedFiltersStore/SavedFilters.conf';
import { useGetFilterIdByIndex, useGetSavedFilter, useSavedFilters } from 'Stores/SavedFiltersStore/SavedFiltersStore';
import { applyFilterStoreDefaults } from 'Stores/StoresContext/StoresContext.helpers';
import { getObjectIndex, isObjectUnique } from 'Utilities/helpers';

interface SavedSearchMatch {
	isUniqueFilter: boolean;
	matchedFilterId?: number;
	matchedFilterName?: string;
}

/**
 * Pure derivation of whether the current filters match a saved search.
 * Returns the matched id/name and whether the filter set is "unique"
 * (differs from both all saved searches and the defaults).
 */
export const useSavedSearchMatch = (): SavedSearchMatch => {
	const filters = useFilters();
	const savedFilters = useSavedFilters();
	const getFilterIdByIndex = useGetFilterIdByIndex();
	const getSavedFilter = useGetSavedFilter();

	const savedFiltersData = useMemo(() => savedFilters.map((filter) => filter.filterData), [savedFilters]);

	const matchedFilterId = useMemo(() => {
		const matchIndex = getObjectIndex({
			ignoreArrayOrder: true,
			ignoreFields: OMITTED_FIELDS,
			object: filters as unknown as Record<string, unknown>,
			otherObjects: savedFiltersData as unknown as Record<string, unknown>[],
		});

		return matchIndex > -1 ? getFilterIdByIndex(matchIndex) : undefined;
	}, [filters, savedFiltersData, getFilterIdByIndex]);

	const isUniqueFilter = useMemo(() => {
		const defaultFilters = applyFilterStoreDefaults();

		return isObjectUnique({
			ignoreArrayOrder: true,
			ignoreFields: OMITTED_FIELDS,
			object: omit(filters, OMITTED_FIELDS) as unknown as Record<string, unknown>,
			otherObjects: [
				...(savedFiltersData as unknown as Record<string, unknown>[]),
				omit(defaultFilters, OMITTED_FIELDS) as unknown as Record<string, unknown>,
			],
		});
	}, [filters, savedFiltersData]);

	const matchedFilter = matchedFilterId ? getSavedFilter(matchedFilterId) : undefined;

	return { isUniqueFilter, matchedFilterId, matchedFilterName: matchedFilter?.name };
};
